import {
  createColumnHelper, SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ModalEvaluationCreate from '@/app/evaluations/_modalEvaluationCreate/modalEvaluationCreate';
import { Button } from '@/components/button/button';
import { DeprecationToggle } from '@/components/deprecationToggle/deprecationToggle';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { StatusIcon } from '@/components/statusIcon/statusIcon';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useCreateEvaluation,useEvaluationsGet } from '@/hooks/queries/useEvaluations';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { DetailRunInfo, EvaluationCreateReq, EvaluationWithDetails } from '@/types/evals';
import {
  dateFilterToISO, OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum
} from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<EvaluationWithDetails<DetailRunInfo>>();
const SERVER_SORT_COLUMNS: Record<string, string> = { name: 'name', created_at: 'created_at' };

export default function EvaluationsPage() {
  const navigate = useNavigate();
  const [ showModalCreate, setShowModalCreate ] = useState<boolean>( false );
  const [ useLocalZone ] = useLocalZonePreference();
  const [ showDeprecated, setShowDeprecated ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [ { id: 'created_at', desc: true } ] );
  const [ pageSize, setPageSize ] = useState( 30 );
  const { cursors, currentCursorIdx, currentCursor, currentPage, canGoPrev, goNext, goPrev, reset: resetCursor } = useCursorPagination();
  const [ nameFilter, setNameFilter ] = useState( '' );
  const [ startDate, setStartDate ] = useState( '' );
  const [ endDate, setEndDate ] = useState( '' );
  const [ committedFilters, setCommittedFilters ] = useState( {
    name: '',
    startDate: '',
    endDate: ''
  } );

  const activeSort = sorting[0] as SortingState[0] | undefined;

  const { data, isLoading, isError } = useEvaluationsGet( {
    limit: pageSize,
    cursor: currentCursor ?? undefined,
    name: committedFilters.name || undefined,
    is_deprecated: showDeprecated ? undefined : false,
    created_at_from: committedFilters.startDate ? dateFilterToISO( committedFilters.startDate, useLocalZone ) : undefined,
    created_at_to: committedFilters.endDate ? dateFilterToISO( committedFilters.endDate, useLocalZone, true ) : undefined,
    include_details: true,
    sort_by: currentCursor ? undefined : ( activeSort ? SERVER_SORT_COLUMNS[ activeSort.id ] : undefined ),
    sort_order: currentCursor ? undefined : ( activeSort ? ( activeSort.desc ? 'desc' : 'asc' ) : undefined )
  } );

  const pageItems = data?.items ?? [];
  const createEvaluationMutation = useCreateEvaluation();

  /* COLUMNS */
  const columns = useMemo( () => [
    columnHelper.accessor( ( row ) => row.evaluation.name, {
      id: 'name',
      header: 'Name',
      size: 250,
      cell: info => {
        const evalData = info.row.original;
        return (
          <span data-testid={ `evaluation-row-${ evalData.evaluation.id }` }>
            {info.getValue()}
            { evalData.evaluation.is_deprecated && (
              <span className='deprecated_tag'>(deprecated)</span>
            ) }
          </span>
        );
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( ( row ) => row.evaluation.description, {
      id: 'description',
      header: 'Description',
      size: 250,
      cell: info => info.getValue() || '-',
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor(
      ( row ) => row.details?.last_run_date || row.evaluation.created_at,
      {
        id: 'lastRunDate',
        header: 'Last Run',
        size: 250,
        cell: info => {
          const details = info.row.original.details;
          if ( !details || details.run_count < 1 ) {
            return <span>-</span>;
          }
          const status = details?.last_run_status;
          return (
            <div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
              <StatusIcon status={ status } />
              <TimeFormatter
                sourceType={ TimeSourceTypeEnum.ISO }
                value={ info.getValue() }
                opts={ { useLocalZone } }
                outputFormat={ OutputFormatEnum.STRING_SINGLE }
              />
            </div>
          );
        },
        enableSorting: false,
        meta: {
          classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
        }
      } ),
    columnHelper.accessor( ( row ) => row.evaluation.created_at, {
      id: 'created_at',
      header: 'Created',
      size: 200,
      cell: info => {
        return (
          <TimeFormatter
            sourceType={ TimeSourceTypeEnum.ISO }
            value={ info.getValue() }
            opts={ { useLocalZone } }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
          />
        );
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( ( row ) => row.details?.run_count || 0, {
      id: 'runCount',
      header: '# Runs',
      size: 80,
      cell: info => {
        const evalData = info.row.original;
        return (
          <span data-testid={ `evaluation-run-count-${ evalData.evaluation.id }` }>
            {info.getValue()}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 8, styles: ColumnModEnums.CENTER } )
      }
    } )
  ], [ useLocalZone ] );

  const handleRowClick = useCallback( ( evalData: EvaluationWithDetails<DetailRunInfo> ) => {
    const params = new URLSearchParams( { eid: evalData.evaluation.id } );
    navigate( `/evaluations/runs/?${ params.toString() }` );
  }, [ navigate ] );

  const handleNextPage = useCallback( () => {
    if ( data?.pagination.next_cursor ) goNext( data.pagination.next_cursor );
  }, [ data?.pagination.next_cursor, goNext ] );

  const tableState = useMemo( () => ( { sorting } ), [ sorting ] );

  const table = useNativeTable<EvaluationWithDetails<DetailRunInfo>>( {
    data: pageItems,
    columns,
    state: tableState,
    onSortingChange: ( updater ) => {
      setSorting( updater );
      resetCursor();
    },
    manualPagination: true,
    manualSorting: true
  } );

  const handleApplyFilters = useCallback( () => {
    setCommittedFilters( { name: nameFilter, startDate, endDate } );
    resetCursor();
  }, [ nameFilter, startDate, endDate, resetCursor ] );

  const handleClearFilters = useCallback( () => {
    setNameFilter( '' );
    setStartDate( '' );
    setEndDate( '' );
    setCommittedFilters( { name: '', startDate: '', endDate: '' } );
    resetCursor();
  }, [ resetCursor ] );

  /* HANDLERS */
  const onEvaluationCreate = ( args: EvaluationCreateReq ) => {
    createEvaluationMutation.mutate(
      args,
      {
        onError: ( error ) => {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create evaluation';
          console.error( errorMessage );
          toast.error( errorMessage );
        }
      }
    );
  };

  const isPending = createEvaluationMutation.isPending;
  const isSuccess = createEvaluationMutation.isSuccess;

  const handleModalClose = useCallback( () => {
    if ( isPending ) return;
    setShowModalCreate( false );
    createEvaluationMutation.reset();
  }, [ isPending, createEvaluationMutation ] );

  return (
    <div className='page'>
      <ToastContainer />
      <>
        {showModalCreate && (
          <ModalEvaluationCreate
            cb={ onEvaluationCreate }
            onClose={ handleModalClose }
            isPending={ isPending }
            isSuccess={ isSuccess }
          />
        )}

        <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
          <span className='info_name'>
            <Heading as="h2">Evaluations</Heading>
          </span>
          <span className='info_actions'>
            <DeprecationToggle
              entityNamePlural='evaluations'
              showDeprecated={ showDeprecated }
              onChange={ ( next ) => {
                setShowDeprecated( next );
                resetCursor();
              } }
            />
            <Button variant='primary' icon={ <Icon icon={ FiPlus } /> } data-testid='create-evaluation-button' onClick={ () => setShowModalCreate( !showModalCreate ) }>Create Evaluation</Button>
          </span>
        </div>

        {!isLoading && !isError && (
          <div className={ clsx( 'layout__row', 'pad', 'filters' ) }>
            <div className='filter_field'>
              <Input
                id="name-filter"
                type='text'
                label='Filter by name'
                placeholder='Filter by name...'
                value={ nameFilter }
                onChange={ ( e ) => setNameFilter( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
              />
            </div>
            <div className='filter_field'>
              <Input
                id="start-date-filter"
                type='date'
                label='Start date'
                value={ startDate }
                onChange={ ( e ) => setStartDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by created date (start)"
              />
            </div>
            <div className='filter_field'>
              <Input
                id="end-date-filter"
                type='date'
                label='End date'
                value={ endDate }
                onChange={ ( e ) => setEndDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by created date (end)"
              />
            </div>
            <Button
              variant='secondary'
              onClick={ handleClearFilters }
              title="Clear all filters"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
            <Button
              variant='primary'
              onClick={ handleApplyFilters }
              title="Apply filters"
              data-testid="apply-filters-button"
            >
              Apply Filters
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className={ clsx( 'layout__row', styles.layout__evals ) }>
            <Loader />
          </div>
        ) : isError ? (
          <div className={ clsx( 'layout__row', styles.layout__evals ) } data-testid='evaluations-error'>
            <div className={ clsx( styles.evals_error ) }>
              Error loading evaluations
            </div>
          </div>
        ) : (
          <div className={ styles.layout__evals } data-testid='evaluations-table-container'>
            <NativeTable
              table={ table }
              tbodyTestId="table-body-evaluations"
              onRowClick={ handleRowClick }
              cursorPagination={ {
                hasMore: data?.pagination.has_more ?? false,
                canGoPrev,
                currentPage,
                pageSize,
                onNextPage: handleNextPage,
                onPrevPage: goPrev,
                onPageSizeChange: ( size ) => { setPageSize( size ); resetCursor(); }
              } }
            />
          </div>
        )}
        {isPending && (
          <div>
            <Loader />
          </div>
        )}
      </>
    </div>
  );
}
